import { datadogRum } from '@datadog/browser-rum'

import { processOoxmlContent } from './ooxml-utils'

const OOXML_LOOKAROUND_PARAGRAPHS = 3
const DEFAULT_LOOKAROUND_CHARS = 1000

/**
 * Finds the paragraph containing the cursor using binary search.
 * @returns Information about the paragraph containing the cursor.
 */
export const findParagraphContainingCursor = async (
  context: Word.RequestContext
): Promise<{
  targetParagraph: Word.Paragraph | null
  currentIndex: number
  previousParagraphIndex: number
  nextParagraphIndex: number
}> => {
  const paragraphs = context.document.body.paragraphs
  paragraphs.load()
  const selection = context.document.getSelection()
  await context.sync()

  let left = 0
  let right = paragraphs.items.length - 1
  let targetParagraph: Word.Paragraph | null = null
  let currentIndex = -1
  let previousParagraphIndex = -1
  let nextParagraphIndex = -1

  // Binary search to find the paragraph containing the cursor
  while (left <= right) {
    const mid = Math.floor((left + right) / 2)
    const comparison = paragraphs.items[mid]
      .getRange()
      .compareLocationWith(selection)
    // eslint-disable-next-line office-addins/no-context-sync-in-loop
    await context.sync()
    if (
      [
        Word.LocationRelation.contains,
        Word.LocationRelation.containsStart,
        Word.LocationRelation.containsEnd,
        Word.LocationRelation.overlapsAfter,
        Word.LocationRelation.overlapsBefore,
      ].includes(comparison.value)
    ) {
      currentIndex = mid
      nextParagraphIndex = mid + 1
      previousParagraphIndex = mid - 1
      targetParagraph = paragraphs.items[mid]
      break
    } else if (comparison.value === Word.LocationRelation.adjacentBefore) {
      currentIndex = mid + 1
      previousParagraphIndex = mid
      targetParagraph = paragraphs.items[mid + 1]
      nextParagraphIndex = mid + 2
      break
    } else if (comparison.value === Word.LocationRelation.adjacentAfter) {
      currentIndex = mid - 1
      previousParagraphIndex = mid - 2
      targetParagraph = paragraphs.items[mid - 1]
      nextParagraphIndex = mid
      break
    } else if (comparison.value === Word.LocationRelation.after) {
      right = mid - 1
    } else {
      left = mid + 1
    }
  }

  return {
    targetParagraph,
    currentIndex,
    previousParagraphIndex,
    nextParagraphIndex,
  }
}

/**
 * Runs a binary search over the paragraphs in the document to find the paragraph
 * containing the cursor, extending in both directions to get the specified number
 * of characters.
 * @param numChars Number of characters to retrieve.
 * @returns Text and OOXML before and after the cursor.
 */
export const getTextAroundCursor = async (
  numChars: number = DEFAULT_LOOKAROUND_CHARS
): Promise<{
  textBefore: string
  textAfter: string
  textInParagraph: string
}> => {
  return await Word.run(async (context) => {
    datadogRum.startDurationVital('getTextAroundCursor')

    const selection = context.document.getSelection()
    selection.load('text')
    await context.sync()

    const { targetParagraph, previousParagraphIndex, nextParagraphIndex } =
      await findParagraphContainingCursor(context)

    const paragraphs = context.document.body.paragraphs

    if (!targetParagraph) {
      datadogRum.stopDurationVital('getTextAroundCursor')
      return {
        textBefore: '\n',
        textAfter: '\n',
        textInParagraph: '',
      }
    }

    let beforeText = ''
    let afterText = ''
    let textInParagraph = ''

    if (targetParagraph) {
      targetParagraph.load('text')
      await context.sync()
      const rangeBeforeCursor = selection
        .getRange('Start')
        .expandTo(targetParagraph.getRange('Start'))
      const rangeAfterCursor = selection
        .getRange('End')
        .expandTo(targetParagraph.getRange('End'))
      rangeBeforeCursor.load('text')
      rangeAfterCursor.load('text')
      await context.sync()
      beforeText = rangeBeforeCursor.text
      afterText = rangeAfterCursor.text
      textInParagraph = targetParagraph.text
    }

    let prevIndex = previousParagraphIndex
    while (beforeText.length < numChars && prevIndex >= 0) {
      const previousParagraph = paragraphs.items[prevIndex]
      previousParagraph.load('text')
      // eslint-disable-next-line office-addins/no-context-sync-in-loop
      await context.sync()
      if (previousParagraph.text.length) {
        beforeText = previousParagraph.text + '\n' + beforeText
      }
      prevIndex--
    }

    let nextIndex = nextParagraphIndex
    while (afterText.length < numChars && nextIndex < paragraphs.items.length) {
      const nextParagraph = paragraphs.items[nextIndex]
      nextParagraph.load('text')
      // eslint-disable-next-line office-addins/no-context-sync-in-loop
      await context.sync()
      if (nextParagraph.text.length) {
        afterText += '\n' + nextParagraph.text
      }
      nextIndex++
    }

    datadogRum.stopDurationVital('getTextAroundCursor')
    return {
      textAfter: afterText.slice(0, numChars) || '\n',
      textBefore: beforeText.slice(-numChars) || '\n',
      textInParagraph,
    }
  })
}

/**
 * Returns the OOXML content of paragraphs around the cursor.
 * @returns OOXML content of the paragraph before and after the cursor.
 */
export const getOoxmlAroundCursor = async (): Promise<{
  before: string
  after: string
}> => {
  return await Word.run(async (context) => {
    datadogRum.startDurationVital('getOoxmlAroundCursor')
    const { previousParagraphIndex, nextParagraphIndex } =
      await findParagraphContainingCursor(context)
    const paragraphs = context.document.body.paragraphs

    if (paragraphs.items.length === 0) {
      datadogRum.stopDurationVital('getOoxmlAroundCursor')
      return {
        before: '',
        after: '',
      }
    }

    // Select from startParagraphIndex to previousParagraphIndex
    const startParagraphIndex = Math.max(
      0,
      previousParagraphIndex - OOXML_LOOKAROUND_PARAGRAPHS
    )
    let startParagraphRange = paragraphs.items[startParagraphIndex].getRange()
    startParagraphRange = startParagraphRange.expandTo(
      paragraphs.items[startParagraphIndex].getRange('End')
    )
    const beforeOoxml = startParagraphRange.getOoxml()

    // Select from nextParagraphIndex to endParagraphIndex
    const endParagraphIndex = Math.min(
      paragraphs.items.length - 1,
      nextParagraphIndex + OOXML_LOOKAROUND_PARAGRAPHS
    )
    let endParagraphRange = paragraphs.items[endParagraphIndex].getRange()
    endParagraphRange = endParagraphRange.expandTo(
      paragraphs.items[endParagraphIndex].getRange('Start')
    )
    const afterOoxml = endParagraphRange.getOoxml()
    await context.sync()

    datadogRum.stopDurationVital('getOoxmlAroundCursor')
    return {
      // eslint-disable-next-line office-addins/load-object-before-read
      before: processOoxmlContent(beforeOoxml.value),
      // eslint-disable-next-line office-addins/load-object-before-read
      after: processOoxmlContent(afterOoxml.value),
    }
  })
}

/**
 * Returns a single, spanning range of the OOXML content of the paragraphs around
 * the cursor. This is different from getOoxmlAroundCursor, which returns two
 * separate ranges.
 * @returns OOXML content of the paragraphs around the cursor.
 */
export const getOoxmlContainingCursor = async (): Promise<string> => {
  return await Word.run(async (context) => {
    const { previousParagraphIndex, nextParagraphIndex } =
      await findParagraphContainingCursor(context)

    const paragraphs = context.document.body.paragraphs
    const startIndex = Math.max(0, previousParagraphIndex)
    const endIndex = Math.min(paragraphs.items.length - 1, nextParagraphIndex)

    let surroundingRange
    surroundingRange = paragraphs.items[startIndex].getRange()
    surroundingRange = surroundingRange.expandTo(
      paragraphs.items[endIndex].getRange('End')
    )

    const surroundingOoxml = surroundingRange.getOoxml()
    await context.sync()

    // eslint-disable-next-line office-addins/load-object-before-read
    return surroundingOoxml.value
  })
}
